import { useCallback, useRef } from 'react';

import { useConfirmUnsavedNavigation } from '@/shared/hooks/app/useConfirmUnsavedNavigation';
import { useUnsavedChangesGuard } from '@/shared/hooks/app/useUnsavedChangesGuard';
import { objects } from '@/shared/utils/objects';

import { cleanObject } from './helpers';
import { Cleanable } from './types';

type ClosingFormConfirmHookProps<T> = {
  onConfirm: (values: Partial<T>, skipRoute: boolean) => Promise<void>;
  onClose: () => void;
  onValidate: () => Promise<boolean>;
  getCurrentValues: () => T;
  readonly initValues?: T;
  formId: string;
};

export const useClosingFormConfirm = <T extends Cleanable>({
  onConfirm,
  onClose,
  onValidate,
  getCurrentValues,
  initValues,
  formId,
}: ClosingFormConfirmHookProps<T>) => {
  const initValuesRef = useRef<T | undefined>(cleanObject(initValues));
  const confirmUnsavedNavigation = useConfirmUnsavedNavigation();

  const shouldSkipRoutingRef = useRef(true);

  const shouldConfirmChanges = useCallback((currentValues?: T) => {
    if (!initValuesRef.current || !currentValues) return false;

    return !objects.isEqual(initValuesRef.current, cleanObject(currentValues));
  }, []);

  const hasUnsavedChanges = useCallback(() => {
    return shouldConfirmChanges(getCurrentValues());
  }, [getCurrentValues, shouldConfirmChanges]);

  useUnsavedChangesGuard({
    hasUnsavedChanges,
    onCancel: onClose,
    onConfirm: () => onConfirm(getCurrentValues(), shouldSkipRoutingRef.current),
    onTrigger: onValidate,
    formId,
  });

  const handleCancel = async () => {
    const shouldConfirm = hasUnsavedChanges();
    shouldSkipRoutingRef.current = false;

    if (!shouldConfirm) {
      onClose();
      return;
    }

    await confirmUnsavedNavigation({
      type: 'unsavedChanges',
      formId,
      shouldConfirm,
    });
  };

  return { handleCancel };
};
